import { Award, Briefcase, FileText, KeyRound, MailOpen, MousePointerClick, ShieldCheck, UserPlus, Users } from 'lucide-react';
import { cn } from '../../lib/utils';

// DCS Client Portal — brand + benefit panel (portal-brand-copy-2026-09-06).
//
// Presentational only: no fetch, no router, no state, no new npm dependency.
// The only external asset is the DCS logo already used by components/layout/Sidebar.js.
//
//   <PortalBrandPanel variant="login" />                     — the tall panel beside the sign-in box
//   <PortalBrandPanel variant="home" showLogo={false} />     — the slim strip under the portal header
//   <PortalBrandPanel variant="below" />                     — client-login-v2 (7 Sep): the wide, spread-out
//                                                              content UNDER a centred sign-in card: headline,
//                                                              four benefit tiles across, how-it-works in three
//                                                              steps, who-uses-it, trust line, credential
//
// EVERY WORD A CLIENT READS IS IN PORTAL_COPY BELOW. Edit it there and nowhere else;
// claude/portal-copy-2026-09-06.md lists the same lines with a rationale for each.

export const DCS_LOGO_URL =
  'https://customer-assets.emergentagent.com/job_talent-match-129/artifacts/10z830dt_Daruwallas%20Consultancy%20Services%20LLP%20Logo.png';

// Brand tokens — the same set the public assessment/video pages use.
// gold #C9A54E is index.css's --accent (42 44% 54%); #FDFCF9 cream ground; #E5E1D5 hairline.
const GOLD = '#C9A54E';
const GOLD_DEEP = '#A8863A';
const GOLD_TINT = '#F8F2E2';
const HAIRLINE = '#E5E1D5';
const CREAM = '#FDFCF9';

export const PORTAL_COPY = {
  kicker: 'Client Portal',
  wordmark: 'Daruwalla Consultancy Services',

  // --- the three headline lines (variant B for each is in claude/portal-copy-2026-09-06.md)
  headline: 'Every role you have given us, in one place you can open any time.',
  subhead:
    'This is the DCS client portal. Sign in with your work email and you can see where each of your open positions has reached, read the profiles we have shortlisted, and pull up your agreement — without writing to anyone.',
  homeLine: 'Your open roles, the profiles we have shared and your paperwork — all on this one page.',

  benefits: [
    {
      key: 'requirements',
      icon: Briefcase,
      title: 'Every requirement, live',
      body: 'Each role you have given us with its stage, openings, location and how many profiles we have shared so far.',
    },
    {
      key: 'candidates',
      icon: Users,
      title: 'Shortlists you can just read',
      body: 'Profiles open right here — experience, notice period, skills, where each one has reached. No attachments, no thread to dig through.',
    },
    {
      key: 'paperwork',
      icon: FileText,
      title: 'Agreement and papers on file',
      body: 'Your signed agreement, when it went out and when it came back, ready to open. Invoices join it next.',
    },
    {
      key: 'team',
      icon: UserPlus,
      title: 'Add your own people',
      body: 'Bring in your hiring managers and HR colleagues yourself. They get their own sign-in the same minute.',
    },
  ],

  closing:
    'Nobody should have to ring their recruiter to find out where a role stands. From here, you can just look.',
  trust:
    'Candidate details are shared only for the roles you have given us, only with the people on your team, and only as far as that role’s sharing settings allow.',

  // --- variant="below" only (client-login-v2)
  belowKicker: 'What you get once you are in',
  steps: [
    {
      key: 'invite',
      icon: MailOpen,
      title: 'Your consultant invites you',
      body: 'DCS adds your work email (and mobile, if you like) to your company’s portal. Nothing to install, nothing to buy.',
    },
    {
      key: 'signin',
      icon: KeyRound,
      title: 'Sign in the way you prefer',
      body: 'A password, a one-time code by email, or a one-time code on WhatsApp — pick whichever is quickest for you today.',
    },
    {
      key: 'look',
      icon: MousePointerClick,
      title: 'Open a role and just look',
      body: 'Every requirement, every shortlisted profile and your agreement are laid out on one page. Add colleagues from the Team tab.',
    },
  ],
  whoUses: {
    title: 'Built for the people who actually run the hiring',
    body:
      'HR heads use it to see every open mandate at a glance. Talent-acquisition teams use it to read shortlists the moment we share them, without a mail thread. Hiring managers use it to check where a role stands before a review meeting — and to bring in the colleague who will interview. Everyone sees the same page, updated by your DCS consultant as the search moves.',
  },

  credential: 'Great Place to Work Certified 2025',
  legalName: 'Daruwalla Consultancy Services LLP',
  place: 'Fort, Mumbai',
  contact: 'care@daruwallas.com',
  website: 'daruwallas.com',
  helpLine: 'Stuck at the sign-in, or need someone added? Write to',
};

const hideOnError = (e) => {
  if (e && e.currentTarget) e.currentTarget.style.display = 'none';
};

function BrandLockup({ logoUrl, size = 'lg' }) {
  const big = size === 'lg';
  return (
    <div className="flex items-center gap-3" data-testid="portal-brand-lockup">
      <img
        src={logoUrl}
        alt="Daruwalla Consultancy Services"
        onError={hideOnError}
        className={cn('object-contain rounded shrink-0', big ? 'w-11 h-11' : 'w-8 h-8')}
      />
      <div className="min-w-0">
        <div
          className={cn(
            'font-semibold text-slate-900 leading-tight truncate',
            big ? 'text-base sm:text-lg' : 'text-sm'
          )}
          style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
        >
          {PORTAL_COPY.wordmark}
        </div>
        <div
          className={cn('uppercase font-bold tracking-[0.28em]', big ? 'text-[11px]' : 'text-[10px]')}
          style={{ color: GOLD_DEEP }}
        >
          {PORTAL_COPY.kicker}
        </div>
      </div>
    </div>
  );
}

function CredentialChip({ compact = false }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 rounded-full border font-semibold whitespace-nowrap',
        compact ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1.5 text-xs'
      )}
      style={{ borderColor: GOLD, backgroundColor: GOLD_TINT, color: GOLD_DEEP }}
      data-testid="portal-brand-credential"
    >
      <Award className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} aria-hidden="true" />
      {PORTAL_COPY.credential}
    </span>
  );
}

function TrustLine({ compact = false }) {
  return (
    <p
      className={cn('flex items-start gap-2 text-slate-500', compact ? 'text-[11px]' : 'text-xs')}
      data-testid="portal-brand-trust"
    >
      <ShieldCheck
        className={cn('shrink-0 mt-px', compact ? 'w-3.5 h-3.5' : 'w-4 h-4')}
        style={{ color: GOLD_DEEP }}
        aria-hidden="true"
      />
      <span>{PORTAL_COPY.trust}</span>
    </p>
  );
}

/**
 * variant   'login' (tall panel) | 'home' (slim strip) | 'below' (wide content under a centred card).
 *            Anything else falls back to 'login'.
 * clientName  optional — greets the signed-in company on the home strip.
 * showLogo    the panel carries the DCS mark + wordmark; pass false where the page header already does.
 * logoUrl     override only if the asset ever moves.
 */
export default function PortalBrandPanel({
  variant = 'login',
  clientName = '',
  showLogo = true,
  logoUrl = DCS_LOGO_URL,
  className = '',
}) {
  const isHome = variant === 'home';
  const isBelow = variant === 'below';

  if (isBelow) {
    return (
      <section className={cn('w-full', className)} data-testid="portal-brand-panel" data-variant="below">
        {showLogo ? (
          <div className="flex justify-center">
            <BrandLockup logoUrl={logoUrl} size="lg" />
          </div>
        ) : null}

        {/* headline — generous, centred, lots of air */}
        <div className={cn('max-w-3xl mx-auto text-center', showLogo ? 'mt-8' : '')}>
          <p className="text-[11px] uppercase font-bold tracking-[0.28em]" style={{ color: GOLD_DEEP }}>
            {PORTAL_COPY.belowKicker}
          </p>
          <h2
            className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight text-balance"
            data-testid="portal-brand-headline"
          >
            {PORTAL_COPY.headline}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed" data-testid="portal-brand-subhead">
            {PORTAL_COPY.subhead}
          </p>
        </div>

        {/* four benefit tiles across on desktop, one per row on a phone */}
        <div
          className="mt-10 sm:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5"
          data-testid="portal-brand-benefits"
        >
          {PORTAL_COPY.benefits.map(({ key, icon: Icon, title, body }) => (
            <div
              key={key}
              className="rounded-2xl border bg-white p-5 sm:p-6 shadow-sm"
              style={{ borderColor: HAIRLINE }}
              data-testid={`portal-benefit-${key}`}
            >
              <span
                className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: GOLD_TINT, color: GOLD_DEEP }}
              >
                <Icon className="w-5 h-5" aria-hidden="true" />
              </span>
              <h3 className="mt-4 font-semibold text-slate-900 text-base leading-snug">{title}</h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        {/* how it works — three steps */}
        <div className="mt-12 sm:mt-16" data-testid="portal-brand-steps">
          <p className="text-[11px] uppercase font-bold tracking-[0.28em] text-center" style={{ color: GOLD_DEEP }}>
            How it works
          </p>
          <h3 className="mt-3 text-xl sm:text-2xl font-bold text-slate-900 text-center">Three steps, no training needed</h3>
          <ol className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {PORTAL_COPY.steps.map(({ key, icon: Icon, title, body }, i) => (
              <li
                key={key}
                className="relative rounded-2xl border p-5 sm:p-6"
                style={{ borderColor: HAIRLINE, backgroundColor: CREAM }}
                data-testid={`portal-step-${key}`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white"
                    style={{ backgroundColor: GOLD_DEEP }}
                  >
                    {i + 1}
                  </span>
                  <Icon className="w-5 h-5" style={{ color: GOLD_DEEP }} aria-hidden="true" />
                </div>
                <h4 className="mt-4 font-semibold text-slate-900 text-base">{title}</h4>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
              </li>
            ))}
          </ol>
        </div>

        {/* who uses it */}
        <div
          className="mt-12 sm:mt-16 rounded-2xl border bg-white p-6 sm:p-8 lg:p-10 shadow-sm"
          style={{ borderColor: HAIRLINE }}
          data-testid="portal-brand-who"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10 items-start">
            <div>
              <p className="text-[11px] uppercase font-bold tracking-[0.28em]" style={{ color: GOLD_DEEP }}>
                Who uses it
              </p>
              <h3 className="mt-3 text-xl sm:text-2xl font-bold text-slate-900 leading-snug">{PORTAL_COPY.whoUses.title}</h3>
            </div>
            <div className="lg:col-span-2">
              <p className="text-base text-slate-600 leading-relaxed">{PORTAL_COPY.whoUses.body}</p>
              <blockquote
                className="mt-6 border-l-4 pl-4 py-2 rounded-r-lg text-sm sm:text-base text-slate-600 italic"
                style={{ borderColor: GOLD, backgroundColor: CREAM }}
                data-testid="portal-brand-closing"
              >
                {PORTAL_COPY.closing}
              </blockquote>
            </div>
          </div>
        </div>

        {/* trust + credential */}
        <div className="mt-10 sm:mt-12 max-w-3xl mx-auto">
          <div className="rounded-xl border p-4 sm:p-5" style={{ borderColor: HAIRLINE, backgroundColor: CREAM }}>
            <TrustLine />
          </div>
          <div className="mt-6 flex flex-col sm:flex-row flex-wrap items-center justify-center gap-x-5 gap-y-3 text-center">
            <CredentialChip />
            <p className="text-xs text-slate-500">
              {PORTAL_COPY.legalName} · {PORTAL_COPY.place}
            </p>
          </div>
          <p className="mt-5 text-xs text-slate-500 text-center" data-testid="portal-brand-help">
            {PORTAL_COPY.helpLine}{' '}
            <a href={`mailto:${PORTAL_COPY.contact}`} className="font-medium hover:underline" style={{ color: GOLD_DEEP }}>
              {PORTAL_COPY.contact}
            </a>
            .
          </p>
        </div>
      </section>
    );
  }

  if (isHome) {
    return (
      <section
        className={cn('rounded-xl border bg-white shadow-sm overflow-hidden', className)}
        style={{ borderColor: HAIRLINE }}
        data-testid="portal-brand-panel"
        data-variant="home"
      >
        <div className="h-1" style={{ background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD_DEEP} 55%, ${GOLD} 100%)` }} />
        <div className="px-4 py-3.5 sm:px-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              {showLogo ? (
                <img
                  src={logoUrl}
                  alt="Daruwalla Consultancy Services"
                  onError={hideOnError}
                  className="w-8 h-8 object-contain rounded shrink-0"
                />
              ) : null}
              <div className="min-w-0">
                <div className="text-[10px] uppercase font-bold tracking-[0.28em]" style={{ color: GOLD_DEEP }}>
                  {PORTAL_COPY.kicker}
                </div>
                <p className="text-sm text-slate-700 mt-1" data-testid="portal-brand-home-line">
                  {clientName ? `${clientName} — ${PORTAL_COPY.homeLine}` : PORTAL_COPY.homeLine}
                </p>
              </div>
            </div>
            <div className="sm:shrink-0">
              <CredentialChip compact />
            </div>
          </div>
          <div className="mt-3 pt-3 border-t" style={{ borderColor: HAIRLINE }}>
            <TrustLine compact />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={cn('w-full', className)} data-testid="portal-brand-panel" data-variant="login">
      {showLogo ? <BrandLockup logoUrl={logoUrl} size="lg" /> : null}

      <h1
        className={cn(
          'text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight text-balance',
          showLogo ? 'mt-6' : ''
        )}
        data-testid="portal-brand-headline"
      >
        {PORTAL_COPY.headline}
      </h1>
      <p className="mt-3 text-base sm:text-lg text-slate-600 leading-relaxed" data-testid="portal-brand-subhead">
        {PORTAL_COPY.subhead}
      </p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="portal-brand-benefits">
        {PORTAL_COPY.benefits.map(({ key, icon: Icon, title, body }) => (
          <div
            key={key}
            className="flex items-start gap-3 rounded-xl border bg-white p-3.5 sm:p-4 shadow-sm"
            style={{ borderColor: HAIRLINE }}
            data-testid={`portal-benefit-${key}`}
          >
            <span
              className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
              style={{ backgroundColor: GOLD_TINT, color: GOLD_DEEP }}
            >
              <Icon className="w-[18px] h-[18px]" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <h2 className="font-semibold text-slate-900 text-sm leading-snug">{title}</h2>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">{body}</p>
            </div>
          </div>
        ))}
      </div>

      <blockquote
        className="mt-6 border-l-4 pl-4 py-2 rounded-r-lg text-sm sm:text-base text-slate-600 italic"
        style={{ borderColor: GOLD, backgroundColor: CREAM }}
        data-testid="portal-brand-closing"
      >
        {PORTAL_COPY.closing}
      </blockquote>

      <div className="mt-6 rounded-xl border p-3.5" style={{ borderColor: HAIRLINE, backgroundColor: CREAM }}>
        <TrustLine />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-3">
        <CredentialChip />
        <p className="text-xs text-slate-500">
          {PORTAL_COPY.legalName} · {PORTAL_COPY.place}
        </p>
      </div>

      <p className="mt-4 text-xs text-slate-500" data-testid="portal-brand-help">
        {PORTAL_COPY.helpLine}{' '}
        <a
          href={`mailto:${PORTAL_COPY.contact}`}
          className="font-medium hover:underline"
          style={{ color: GOLD_DEEP }}
        >
          {PORTAL_COPY.contact}
        </a>
        .
      </p>
    </section>
  );
}
